import React, { useState, useRef } from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import { useTimer } from '../contexts/TimerContext';

const GlobalTimer = () => {
  const { timerState } = useTimer();
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag if clicking on buttons
    
    setIsDragging(true);
    const rect = timerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('button')) return; // Don't drag if clicking on buttons
    
    setIsDragging(true);
    const touch = e.touches[0];
    const rect = timerRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep timer within viewport bounds
    const maxX = window.innerWidth - 300;
    const maxY = window.innerHeight - 100;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;

    // Keep timer within viewport bounds
    const maxX = window.innerWidth - 300;
    const maxY = window.innerHeight - 100;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Debug timer state
 // console.log('🕐 [GLOBAL TIMER] Render check:', {
 //   isVisible: timerState.isVisible,
 //   seconds: timerState.seconds,
 //   title: timerState.title
 // });

  // Don't show anything when timer is not visible
  if (!timerState.isVisible) {
 //   console.log('🕐 [GLOBAL TIMER] Not visible, returning null');
    return null;
  }

  return (
    <Box sx={{ 
      position: 'fixed', 
      left: position.x, 
      top: position.y, 
      zIndex: 9999,
      pointerEvents: 'auto'
    }}>

      
      {/* Small Delicate Timer */}
      <Box
        ref={timerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #FF4444 0%, #FF6666 100%)',
          color: 'white',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
          boxShadow: '0 4px 12px rgba(255, 68, 68, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #FF3333',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(255, 68, 68, 0.4)',
            transform: 'scale(1.05)'
          },
          transition: 'all 0.2s ease-in-out',
          position: 'relative'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ 
            fontWeight: 'bold', 
            fontSize: '0.875rem', 
            lineHeight: 1,
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }}>
            {Math.floor(timerState.seconds / 60)}:{(timerState.seconds % 60).toString().padStart(2, '0')}
          </Typography>
        </Box>
        
        {/* Small drag indicator */}
        <Box sx={{
          position: 'absolute',
          bottom: 2,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 20,
          height: 2,
          backgroundColor: 'rgba(255,255,255,0.6)',
          borderRadius: 1
        }} />
      </Box>
    </Box>
  );
};

export default GlobalTimer; 